import { useCMSPageField } from '@/cms/fields'
import { useSubscribeParticipant } from '@/domain/groups/hooks'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'

import { useIntl } from '@/intl'

import { Custom500 } from '@/pages/500'
import { CheckoutContextProvider } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { Loader, PageLayout, Wrapper } from '@/scopes/checkout/groups/components/Layout'
import { StatusNotification } from '@/scopes/checkout/groups/components/StatusNotification'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { CheckoutGroupsNotificationError } from '@/scopes/checkout/pages/groups/Components/NotificationError'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import { SpaceBetween, tk, Typography, verticalGap } from '@sunwebgroup/ui-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import * as React from 'react'
import { styled } from 'styled-components'
import { Header } from '../../groups/components/Header'

const queryClient = new QueryClient()

const Content = styled.div`
  max-width: ${tk('breakpoint_desktop_xl')};
`
const MainContent = styled.div`
  ${verticalGap('spacing_lg')}
  display: flex;
  flex-direction: column;
  width: 100%;

  @media ${tk('mq_desktop_md')} {
    margin: 0 auto;
    width: 60%;
  }
`
const DescriptionBlock = styled(SpaceBetween)`
  text-align: center;
`

const LoadingMessage = styled(Typography)`
  text-align: center;
  max-width: 420px;
`

function Page() {
  const sid = useCheckoutSid()
  const { isLoading, data } = useSubscribeParticipant()
  const { isLoading: isLoadingBooking, data: bookingData } = useBookingDraftQuery()
  // const paymentPageUrl = useCMSPageField('Checkout.paymentPageUrl')?.url ?? null
  const intl = useIntl()
  // const handlePaymentButtonClick = () => {
  //   if (paymentPageUrl) {
  //     window.open(
  //       `${paymentPageUrl}?bookingNumber=${data.childBookingNumber}&groupUserId=${data.bookingCustomerId}`,
  //       '_blank',
  //     )
  //   }
  // }
  if (!sid) {
    return <CheckoutGroupsNotificationError />
  }
  return (
    <PageLayout>
      <Header hideLiveSearch />
      <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand="sunweb" />}>
        <Wrapper>
          {isLoading || isLoadingBooking
            ? (
                <SpaceBetween direction="y" spacing={tk('spacing_sm')} alignItems="center">
                  <Loader />
                  <LoadingMessage $variant="body-md-bold">
                    {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_loading' })}
                  </LoadingMessage>
                </SpaceBetween>
              )
            : (
                <Content>
                  <MainContent>
                    {data
                      ? (
                          <SpaceBetween direction="y" spacing={tk('spacing_sm')} alignItems="center">
                            <StatusNotification status={data?.status} groupName={bookingData?.groupInformation?.name} errorReason={data?.errorReason} />
                            {data?.status === 'success'
                              ? (
                                  <>
                                    <Typography>
                                      {intl.formatMessage(
                                        { id: 'groups_checkout_steps_confirm_step_more_description' },
                                        { bookingNumber: data?.childBookingNumber },
                                      )}
                                    </Typography>
                                    <DescriptionBlock direction="y" spacing={tk('spacing_sm')} alignItems="center">
                                      <Typography $variant="body-md-bold">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_title' })}
                                      </Typography>
                                      <Typography
                                        $variant="body-md"
                                        dangerouslySetInnerHTML={{
                                          __html: intl.formatMessage(
                                            { id: 'groups_checkout_steps_confirm_step_more_info_description_1' },
                                            { groupName: `<strong>${bookingData?.groupInformation?.name}</strong>` },
                                          ),
                                        }}
                                      />
                                      <Typography $variant="body-md-bold">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_title_2' })}
                                      </Typography>
                                      <Typography
                                        $variant="body-md"
                                        dangerouslySetInnerHTML={{
                                          __html: intl.formatMessage(
                                            { id: 'groups_checkout_steps_confirm_step_more_info_description_5' },
                                          ),
                                        }}
                                      />
                                      <Typography $variant="body-md">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_description_2' })}
                                      </Typography>
                                      <Typography $variant="body-md">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_description_3' }, { groupName: bookingData?.groupInformation?.name })}
                                      </Typography>
                                      <Typography $variant="body-md">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_description_4' })}
                                      </Typography>
                                      {/* <Button onClick={handlePaymentButtonClick}>
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_pay_button' })}
                                      </Button> */}
                                    </DescriptionBlock>
                                  </>
                                )
                              : null}
                          </SpaceBetween>
                        )
                      : (
                          ''
                        )}
                  </MainContent>
                </Content>
              )}
        </Wrapper>
      </NewRelicErrorBoundary>
    </PageLayout>
  )
}

export default function ConfirmationStep() {
  const router = useRouter()
  const contextItemId = useCMSPageField('contextItemId')
  const [sid, setSid] = React.useState<string | null>('')

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutContextProvider
        value={{
          contextItemId,
          draftId: sid || null,
          setDraftId: setSid,
        }}
      >
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <Page />
        </GroupsDependenciesProvider>
        <DevTools />
      </CheckoutContextProvider>
    </QueryClientProvider>
  )
}
